import { DateInput } from '@core/components/Inputs';
import CurrencyTextField from '@core/components/Inputs/CurrencyTextField';
import { SelectInput } from '@core/components/Select';
import { Collapse, Fade, FormControlLabel, Grid, Switch, Tooltip } from '@material-ui/core';
import SelectConta from 'app/components/Select/SelectConta';
import contasPagarApi from 'app/services/api/contasPagarApi';
import React, { useEffect, useState } from 'react';
import CompraAcrescimosDescontos from './CompraAcrescimosDescontos';

/*
RNE-092 - Preenchimento das formas de pagamento conforme conta

O sistema deverá exibir as formas de pagamento conforme a conta selecionada:
- Quando for selecionada alguma conta que seja do tipo "Caixinha", o sistema só deve exibir na lista a forma de pagamento "Dinheiro".
- Quando for selecionada alguma conta que seja do tipo "Corrente", o sistema deve exibir na lista todas as formas de pagamento, exceto "Dinheiro".
- Quando for selecionada alguma conta que seja do tipo "Poupança", o sistema deve exibir na lista todas as formas de pagamento, exceto "Dinheiro", "Crédito" e "Cheque".

Observação: As contas do tipo "Investimento" não devem ser exibidas na lista.
*/

const FORMAS_PAGAMENTO = {
  CONTA_CAIXINHA: [{ id: 'DINHEIRO', title: 'Dinheiro' }],
  CONTA_CORRENTE: [
    { id: 'CARTAO_CREDITO', title: 'Cartão de crédito' },
    { id: 'CARTAO_DEBITO', title: 'Cartão de débito' },
    { id: 'CHEQUE', title: 'Cheque' },
    { id: 'DEBITO_AUTOMATICO', title: 'Débito automático' },
    { id: 'DEBITO_CONTA', title: 'Débito em conta' },
    { id: 'DOC', title: 'DOC' },
    { id: 'TED', title: 'TED' },
    { id: 'TRANSFERENCIA_BANCARIA', title: 'Transferência bancária' },
    { id: 'BOLETO', title: 'Boleto' },
    { id: 'PIX', title: 'Pix' },
    { id: 'OUTRAS', title: 'Outras' },
  ],
  CONTA_POUPANCA: [
    { id: 'CARTAO_DEBITO', title: 'Cartão de débito' },
    { id: 'DEBITO_AUTOMATICO', title: 'Débito automático' },
    { id: 'DEBITO_CONTA', title: 'Débito em conta' },
    { id: 'DOC', title: 'DOC' },
    { id: 'TED', title: 'TED' },
    { id: 'TRANSFERENCIA_BANCARIA', title: 'Transferência bancária' },
    { id: 'PIX', title: 'Pix' },
    { id: 'OUTRAS', title: 'Outras' },
  ],
  CONTA_CARTAO: [
    { id: 'CARTAO_CREDITO', title: 'Cartão de crédito' },
    { id: 'CARTAO_DEBITO', title: 'Cartão de débito' },
  ],
  CONTA_INVESTIMENTO: [
    { id: 'CARTAO_DEBITO', title: 'Cartão de débito' },
    { id: 'DEBITO_AUTOMATICO', title: 'Débito automático' },
    { id: 'DEBITO_CONTA', title: 'Débito em conta' },
    { id: 'DOC', title: 'DOC' },
    { id: 'TED', title: 'TED' },
    { id: 'TRANSFERENCIA_BANCARIA', title: 'Transferência bancária' },
    { id: 'BOLETO', title: 'Boleto' },
    { id: 'PIX', title: 'Pix' },
    { id: 'OUTRAS', title: 'Outras' },
  ],
};

const CompraPagamentoAVista = ({
  id,
  name,
  onChange,
  onBlur,
  values,
  errors,
  touched,
  submitCount,
  compraPaga,
  valorTotal,
  compraId,
}) => {
  const _errors = {
    dataVencimento: (submitCount > 0 || touched?.dataVencimento) && errors?.dataVencimento,
    dataPagamento: (submitCount > 0 || touched?.dataPagamento) && errors?.dataPagamento,
    dataLiquidacao: (submitCount > 0 || touched?.dataLiquidacao) && errors?.dataLiquidacao,
    conta: (submitCount > 0 || touched?.conta) && errors?.conta,
    formaPagamento: (submitCount > 0 || touched?.formaPagamento) && errors?.formaPagamento,
  };

  const [conta, setConta] = useState(null);

  useEffect(() => {
    // buscando a conta a pagar referente a compra para podermos verificar sua situação
    const fetchConta = async () => {
      try {
        const contaData = await contasPagarApi.buscarPorId(values.contaPagarId);
        setConta(contaData.data);
      } catch (error) {
        console.error('Erro ao buscar conta:', error);
      }
    };

    fetchConta();
  }, [values.contaPagarId]);

  useEffect(() => {
    // se a conta estiver parcial, não podemos marcar as informações da compraPaga mas devemos impedir a edição
    if (conta?.situacao === 'PARCIAL') {
      onChange({
        target: {
          id: `${id}.compraPaga`,
          value: false,
        },
      });
    }
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta]);

  return (
    <>
      <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
        <DateInput
          id={`${id}.dataVencimento`}
          name={`${name}.dataVencimento`}
          onChange={onChange}
          onBlur={onBlur}
          value={values?.dataVencimento || null}
          error={!!_errors?.dataVencimento}
          helperText={_errors?.dataVencimento}
          touched={touched?.dataVencimento}
          required
          fullWidth
          variant="outlined"
          label="Data do vencimento"
          disabled={compraPaga}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
        <Tooltip title="Quando essa opção é acionada, o sistema considera o pagamento da conta principal e das retenções, caso existam">
          <div>
            <FormControlLabel
              control={
                <Switch
                  id={`${id}.compraPaga`}
                  name={`${name}.compraPaga`}
                  value={values?.compraPaga}
                  checked={values?.compraPaga}
                  onChange={onChange}
                  onBlur={onBlur}
                  color="primary"
                  disabled={compraPaga}
                />
              }
              label="Compra paga"
            />
          </div>
        </Tooltip>
      </Grid>
      <Collapse in={values?.compraPaga} style={{ width: '100%' }}>
        <Grid container spacing={3} style={{ marginLeft: 0 }} className="mt-4 pr-24">
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <DateInput
              id={`${id}.dataPagamento`}
              name={`${id}.dataPagamento`}
              onChange={onChange}
              onBlur={onBlur}
              value={values?.dataPagamento || null}
              error={!!_errors?.dataPagamento}
              helperText={_errors?.dataPagamento}
              touched={touched?.dataPagamento}
              required
              fullWidth
              variant="outlined"
              label="Data do pagamento"
              disabled={compraPaga}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <DateInput
              id={`${id}.dataLiquidacao`}
              name={`${id}.dataLiquidacao`}
              onChange={onChange}
              onBlur={onBlur}
              value={values?.dataLiquidacao || null}
              error={!!_errors?.dataLiquidacao}
              helperText={_errors?.dataLiquidacao}
              touched={touched?.dataLiquidacao}
              required
              fullWidth
              variant="outlined"
              label="Data da liquidação"
              disabled={compraPaga}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
            <SelectConta
              id={`${id}.conta`}
              name={`${name}.conta`}
              label="Conta *"
              onChange={onChange}
              onBlur={onBlur}
              value={values?.conta}
              touched={!!_errors?.conta}
              error={_errors?.conta}
              disabled={compraPaga}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
            <SelectInput
              id={`${id}.formaPagamento`}
              name={`${name}.formaPagamento`}
              label="Forma de pagamento"
              onChange={onChange}
              onBlur={onBlur}
              value={values?.formaPagamento}
              options={FORMAS_PAGAMENTO[values?.conta?.tipo || ''] || []}
              error={_errors.formaPagamento}
              touched={!!_errors.formaPagamento}
              required
              disabled={!values?.conta || compraPaga}
            />
          </Grid>
          {values?.formaPagamento === 'CHEQUE' && (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <Fade in={values?.formaPagamento === 'CHEQUE'}>
                <CurrencyTextField
                  outputFormat="string"
                  decimalCharacter=","
                  digitGroupSeparator="."
                  maximumValue="999999999"
                  minimumValue="0"
                  decimalPlaces={2}
                  decimalPlacesShownOnBlur={0}
                  decimalPlacesShownOnFocus={0}
                  currencySymbol=""
                  error={!!errors?.numeroCheque}
                  helperText={errors?.numeroCheque}
                  onChange={(e, value) => {
                    onChange({
                      target: {
                        id: `${id}.numeroCheque`,
                        value: e.target.value === '' ? '' : value,
                      },
                    });
                  }}
                  onBlur={onBlur}
                  id={`${id}.numeroCheque`}
                  name={`${id}.numeroCheque`}
                  value={values?.numeroCheque}
                  fullWidth
                  variant="outlined"
                  label="Número do cheque *"
                  disabled={compraPaga}
                />
              </Fade>
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <FormControlLabel
              label="Incluir acréscimos e descontos"
              control={
                <Switch
                  id={`${id}.acrescimosDescontos`}
                  name={`${id}.acrescimosDescontos`}
                  value={values?.acrescimosDescontos}
                  checked={values?.acrescimosDescontos}
                  color="primary"
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={compraId && compraPaga}
                />
              }
            />
          </Grid>
          {values?.acrescimosDescontos && (
            <CompraAcrescimosDescontos
              id={id}
              values={values}
              handleChange={onChange}
              onBlur={onBlur}
              errors={errors}
              touched={touched}
              disabled={compraId && compraPaga && values?.acrescimosDescontos}
              valorTotal={valorTotal}
            />
          )}
        </Grid>
      </Collapse>
    </>
  );
};

export default CompraPagamentoAVista;
